import { FragmentStyleContainer } from '@/app/components/FragmentStyleContainer'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { CloseFilled } from '@/app/icons/CloseFilled'

import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ICourse } from '@/types/course'
import { Dialog, Radio, RadioGroup, Transition } from '@headlessui/react'
import { useTranslations } from 'next-intl'
import { Fragment, PropsWithChildren } from 'react'
import { Controller, useForm, useFormContext } from 'react-hook-form'

type CourseCohortSelectorModalProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onSubmit'
> & {
  courseInfo: Pick<ICourse, 'cohorts'>
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: FormValues) => Promise<void>
}

type FormValues = {
  selectedCohortId: string
}

export function CourseCohortSelectorModal({
  courseInfo,
  onClose,
  isOpen,
  onSubmit,
  ...props
}: CourseCohortSelectorModalProps) {
  const { formatCohortDate } = useCohortHelpers()

  const { watch } = useFormContext<CoursePageFormValues>()

  const selectedCohortId = watch('selectedCohortId')

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    setValue,
    reset,
  } = useForm<FormValues>()

  const { cohorts } = courseInfo
  const t = useTranslations('CoursePage')
  const user = useAuth()
  const { isCohortActive, isCohortFree } = useCohortHelpers()

  if (!Array.isArray(cohorts) || !cohorts.length) {
    return null
  }

  return (
    <Transition appear show={isOpen} as={Fragment} afterLeave={() => reset()}>
      <Dialog
        {...(props as any)}
        as="div"
        onClose={onClose}
        className={`relative z-[40] w-full font-sans ${props.className ?? ''} `}
      >
        <FragmentStyleContainer>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-modal-bg transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto bg-[#292525a6]">
            <div className="flex min-h-full items-end justify-center md:items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full font-sans">
                  <Dialog.Panel className="relative w-full transform overflow-hidden rounded-2xl rounded-b-none bg-white text-left align-middle shadow-xl transition-all md:w-fit">
                    <Dialog.Title className="flex flex-row p-6 font-title text-h2-mobile md:text-h4">
                      {t('selectCohortToEnrol')}
                      <button
                        type="button"
                        className="ml-auto rounded-lg p-1"
                        onClick={() => onClose()}
                        tabIndex={4}
                      >
                        {<CloseFilled className="h-5 w-5 text-mydra-black" />}
                      </button>
                    </Dialog.Title>
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="flex w-full flex-col gap-8 p-3 py-0 pb-10 md:mt-12"
                    >
                      <div className="flex flex-col gap-6">
                        <Controller
                          control={control}
                          name="selectedCohortId"
                          defaultValue=""
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              tabIndex={2}
                              className="flex flex-col gap-4"
                            >
                              {cohorts.map(
                                ({
                                  startDate,
                                  endDate,
                                  location,
                                  cost,
                                  _id,
                                }) => {
                                  return (
                                    <Radio
                                      key={_id}
                                      value={_id}
                                      className="flex items-center justify-between gap-2 rounded-xl border bg-white px-5 py-6 md:mx-auto md:w-8/12"
                                    >
                                      {() => (
                                        <>
                                          <div className="flex grid-cols-3 items-center gap-4 md:grid md:flex-1">
                                            <CohortDetail title={t('dates')}>
                                              {formatCohortDate({
                                                startDate,
                                                endDate,
                                                location,
                                                cost,
                                              })}
                                            </CohortDetail>
                                          </div>

                                          <PrimaryButton
                                            id={`course-cohorts-selector-button--${user?.isAuthenticated ? 'redirect-to-checkout' : 'redirect-to-signup'}`}
                                            type="submit"
                                            className="ml-auto"
                                            isLoading={
                                              selectedCohortId === _id &&
                                              isSubmitting
                                            }
                                            disabled={
                                              isSubmitting ||
                                              (user?.isAuthenticated &&
                                                !isCohortActive({
                                                  startDate,
                                                  endDate,
                                                }) &&
                                                !isCohortFree({
                                                  cost,
                                                }))
                                            }
                                            onClick={() => {
                                              setValue('selectedCohortId', _id)
                                            }}
                                          >
                                            {t('apply')}
                                          </PrimaryButton>
                                        </>
                                      )}
                                    </Radio>
                                  )
                                }
                              )}
                            </RadioGroup>
                          )}
                        />
                      </div>
                    </form>
                  </Dialog.Panel>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </FragmentStyleContainer>
      </Dialog>
    </Transition>
  )
}

function CohortDetail({
  title,
  children,
  ...props
}: PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement> & { title: string }
>) {
  return (
    <div {...props} className={`flex flex-col gap-1 ${props.className ?? ''}`}>
      <p className="font-title text-sm uppercase leading-6 text-gray-light">
        {title}
      </p>
      <div className="font-sans font-semibold capitalize leading-4 text-black">
        {children}
      </div>
    </div>
  )
}
